import {
  createEnvelope,
  type Envelope,
  type EnvelopeItem,
  type ErrorEvent,
  type SerializedSession,
  type Transport,
} from "@sentry/core";

/** capture context spread into `captureMessage` for a message that must not
 * count against the release-health session (see `applySessionNeutral`) */
export const sessionNeutral = { tags: { session_neutral: true } } as const;

/** `beforeSend` step: a synthetic stack trace (attachStacktrace on a message)
 * lives in `exception`, and any exception value flags the session as errored.
 * For marked events, move the trace to `threads`, which Sentry renders and
 * groups on without touching the session. Real exceptions are left as is. */
export const applySessionNeutral = (event: ErrorEvent): ErrorEvent => {
  if (event.tags?.session_neutral === undefined) return event;

  const { exception, ...rest } = event;
  const values = exception?.values;
  if (!values?.length || !values.every((value) => value.mechanism?.synthetic)) {
    return event;
  }

  return {
    ...rest,
    threads: { values: values.map(({ stacktrace }) => ({ stacktrace })) },
  };
};

/** a session update that changes nothing: still ok, no error, no user */
const isNoopSessionUpdate = ([headers, payload]: EnvelopeItem): boolean => {
  if (headers.type !== "session") return false;

  const { init, status, errors, did } = payload as SerializedSession;
  return init === false && status === "ok" && errors === 0 && did === undefined;
};

/** transport step: the client sends a session update after the first
 * error-type event of a session even when the event was made session-neutral.
 * Strip those; the transport skips an envelope left without items */
export const dropNoopSessionUpdates = (envelope: Envelope): Envelope => {
  const [headers, items] = envelope;
  const kept = items.filter((item) => !isNoopSessionUpdate(item));
  return kept.length === items.length
    ? envelope
    : createEnvelope<Envelope>(headers, kept as Envelope[1]);
};

interface Gate {
  opened: Promise<void>;
  release: () => void;
}

/** opens after `delay` ms, on `release()`, or as soon as the page is hidden
 * so anything held back still leaves with a keepalive fetch */
const pageGate = (delay: number): Gate => {
  let release!: () => void;
  const opened = new Promise<void>((resolve) => {
    release = () => {
      clearTimeout(timer);
      window.removeEventListener("pagehide", release);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      resolve();
    };
    const onVisibilityChange = () =>
      document.visibilityState === "hidden" && release();
    const timer = setTimeout(release, delay);

    window.addEventListener("pagehide", release);
    document.addEventListener("visibilitychange", onVisibilityChange);
  });
  return { opened, release };
};

/** holds every envelope until `delay` ms after creation (or the page being
 * hidden), keeping the boot window free of Sentry requests. A flush opens
 * the gate so the caller's timeout only covers the actual sends */
export const delayedTransport = (
  transport: Transport,
  delay: number
): Transport => {
  const gate = pageGate(delay);
  return {
    send: (envelope) => gate.opened.then(() => transport.send(envelope)),
    flush: (timeout) => {
      gate.release();
      // the held sends are queued ahead of this continuation
      return gate.opened.then(() => transport.flush(timeout));
    },
  };
};
